const BASE_POLICY_CONDITION_ATTRIBUTES = [
    {
        attribute: 'addressId',
        label: 'Address',
        getValues: (context) => context.addressIds,
    },
    {
        attribute: 'clientId',
        label: 'Client',
        getValues: (context) => context.clientIds,
    },
    {
        attribute: 'clientToAddressId',
        label: 'Client/Site link ID',
        getValues: (context) => context.clientToAddressIds,
    },
    {
        attribute: 'partnerId',
        label: 'Partner ID',
        getValues: (context) => context.partnerIds.size ? context.partnerIds : new Set([String(context.partnerId)]),
    },
    {
        attribute: 'partnerGroupId',
        label: 'Partner Group',
        getValues: (context) => context.partnerGroupIds,
    },
    {
        attribute: 'urgency',
        label: 'Conversation urgency',
        getValues: (context) => context.urgencies,
    },
    {
        attribute: 'siteLabelId',
        label: 'Site label',
        getValues: (context) => context.siteLabelIds,
    },
    {
        attribute: 'serviceLevel',
        label: 'Service level',
        getValues: (context) => context.serviceLevels,
    },
    {
        attribute: 'clientSegmentId',
        label: 'Client segment',
        getValues: (context) => context.clientSegmentIds,
    },
    {
        attribute: 'conversationType',
        label: 'Conversation type',
        getValues: (context) => context.conversationTypes,
    },
];
const LEGACY_POLICY_CONDITION_ATTRIBUTES = [
    {
        attribute: 'brandName',
        label: 'Brand (legacy)',
        getValues: (context) => context.clientSegmentIds,
    },
];
export const POLICY_CONDITION_ATTRIBUTES = BASE_POLICY_CONDITION_ATTRIBUTES;
const POLICY_CONDITION_ATTRIBUTE_MAP = new Map([...BASE_POLICY_CONDITION_ATTRIBUTES, ...LEGACY_POLICY_CONDITION_ATTRIBUTES].map((metadata) => [metadata.attribute, metadata]));
function isPolicyConditionAttribute(attribute) {
    return (typeof attribute === 'string' &&
        POLICY_CONDITION_ATTRIBUTE_MAP.has(attribute));
}
export function getApplicablePolicies(policies, context) {
    return policies.filter((policy) => isPolicyApplicable(policy, context));
}
export function isPolicyApplicable(policy, context) {
    if (policy.archivedAt) {
        return false;
    }
    if (policy.validUntil && new Date(policy.validUntil) < new Date()) {
        return false;
    }
    const policyGroupIds = extractPolicyGroupIds(policy.userGroups);
    if (context.userGroupIds && policyGroupIds.length) {
        const hasAccess = policyGroupIds.some((id) => id && context.userGroupIds?.includes(id));
        if (!hasAccess) {
            return false;
        }
    }
    const addressIds = normalizeIdArray(policy.addressIds);
    const clientIds = normalizeIdArray(policy.clientIds);
    const clientToAddressIds = normalizeIdArray(policy.clientToAddressIds);
    const hasScope = addressIds.length || clientIds.length || clientToAddressIds.length;
    const matchesScope = !hasScope ||
        addressIds.some((id) => context.addressIds.has(id)) ||
        clientIds.some((id) => context.clientIds.has(id)) ||
        clientToAddressIds.some((id) => context.clientToAddressIds.has(id));
    if (!matchesScope) {
        return false;
    }
    return evaluateConditions(policy.conditions, context);
}
function extractPolicyGroupIds(userGroups) {
    if (!Array.isArray(userGroups)) {
        return [];
    }
    return userGroups
        .map((group) => {
        if (group && typeof group === 'object') {
            const typedGroup = group;
            return normalizeValue(typedGroup.id) ?? normalizeValue(typedGroup.userGroupId);
        }
        return null;
    })
        .filter((value) => Boolean(value));
}
function evaluateConditions(rawConditions, context) {
    if (!rawConditions)
        return true;
    let conditionGroup = null;
    try {
        conditionGroup = parseConditionGroup(rawConditions);
    }
    catch (error) {
        console.error('Failed to parse support policy conditions', { error });
        return true;
    }
    if (!conditionGroup)
        return true;
    return evaluateConditionElement(conditionGroup, context);
}
function parseConditionGroup(rawConditions) {
    if (!rawConditions)
        return null;
    if (typeof rawConditions === 'object' &&
        rawConditions !== null &&
        'elementType' in rawConditions) {
        return rawConditions;
    }
    if (typeof rawConditions === 'string') {
        return JSON.parse(rawConditions);
    }
    return null;
}
function evaluateConditionElement(condition, context) {
    if (condition.elementType === 'conditionGroup') {
        const expressions = Array.isArray(condition.conditionsElements)
            ? condition.conditionsElements
            : [];
        const results = expressions.map((item) => evaluateConditionElement(item, context));
        return condition.operator === 'or' ? results.some(Boolean) : results.every(Boolean);
    }
    return isDataConditionNode(condition) ? evaluateConditionNode(condition, context) : true;
}
function isDataConditionNode(condition) {
    return (condition.elementType === 'conditionNode' &&
        condition.conditionType === 'data' &&
        typeof condition.attribute === 'string');
}
function evaluateConditionNode(condition, context) {
    const values = normalizeConditionValues(condition.value);
    if (!values.length)
        return false;
    if (!isPolicyConditionAttribute(condition.attribute)) {
        return false;
    }
    const targetSet = getTargetSet(condition.attribute, context);
    if (!targetSet)
        return false;
    switch (condition.operator) {
        case '=':
        case 'in':
        case 'is_one_of':
            return values.some((value) => targetSet.has(value));
        case '!=':
        case 'not_in':
        case 'is_not_one_of':
            return values.every((value) => !targetSet.has(value));
        default:
            return false;
    }
}
function normalizeConditionValues(value) {
    if (Array.isArray(value)) {
        return value.map((v) => normalizeValue(v)).filter((v) => Boolean(v));
    }
    const normalized = normalizeValue(value);
    return normalized ? [normalized] : [];
}
function normalizeValue(value) {
    if (!value && value !== 0)
        return null;
    if (typeof value === 'string')
        return value;
    if (typeof value === 'number')
        return value.toString();
    if (typeof value === 'boolean')
        return value ? 'true' : 'false';
    if (typeof value === 'object' && value && 'value' in value) {
        const nested = value.value;
        return typeof nested === 'string' || typeof nested === 'number' ? String(nested) : null;
    }
    return null;
}
function getTargetSet(attribute, context) {
    const metadata = POLICY_CONDITION_ATTRIBUTE_MAP.get(attribute);
    return metadata ? metadata.getValues(context) : null;
}
const BRACES_REGEX = /^\{|\}$/g;
const BRACKETS_REGEX = /^\[|\]$/g;
const QUOTES_REGEX = /^"+|"+$/g;
/**
 * Normalizes various forms of array-like or string input into a string array of trimmed, non-empty values.
 *
 * - If the input is an array, returns a trimmed string array without empty values.
 * - If the input is a stringified array (e.g., JSON or comma-separated), parses and returns a trimmed string array.
 * - Removes brackets, braces, and quotes if present, and splits comma-separated values.
 * - Returns an empty array for null, undefined, or empty input.
 *
 * @param value - The input data to normalize (array, string, or other).
 * @returns The normalized array of non-empty, trimmed strings.
 *
 * @example
 * normalizeIdArray(['a', ' b ', '', null]);          // ['a', 'b']
 * normalizeIdArray('["x"," y ",""]');                // ['x', 'y']
 * normalizeIdArray('x, y, z');                       // ['x', 'y', 'z']
 * normalizeIdArray('{a, b, c}');                     // ['a', 'b', 'c']
 * normalizeIdArray('[ "foo" , "bar" ]');             // ['foo', 'bar']
 * normalizeIdArray('');                              // []
 * normalizeIdArray(undefined);                       // []
 */
export function normalizeIdArray(value) {
    if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter((item) => item.length > 0);
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed)
            return [];
        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.map((item) => String(item).trim()).filter((item) => item.length > 0);
            }
        }
        catch {
            // ignore parse errors and fall back to comma-splitting
        }
        const sanitized = trimmed.replace(BRACES_REGEX, '').replace(BRACKETS_REGEX, '');
        if (!sanitized)
            return [];
        return sanitized
            .split(',')
            .map((item) => item.replace(QUOTES_REGEX, '').trim())
            .filter((item) => item.length > 0);
    }
    return [];
}
